import { PROFILE_CONSTANTS } from '../constants/profileConstants';

/**
 * Validates individual profile fields
 * @param {string} field - The field name to validate
 * @param {any} value - The field value to validate
 * @param {Function|null} setFieldErrors - Optional state setter function for errors
 * @returns {Object} - Returns error object if setFieldErrors is null
 */
export const validateProfileField = (field, value, setFieldErrors = null) => {
  // Function to validate a field and return updated errors object
  const validateField = (errors = {}) => {
    const updatedErrors = { ...errors };
    
    switch (field) {
      case 'email':
        // Consider a more comprehensive regex or use a library like validator.js
        if (value && !/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(value)) {
          updatedErrors[field] = 'Please enter a valid email address';
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'work_experience':
        // Special validation for work experience array
        if (value && Array.isArray(value)) {
          if (value.length === 0) {
            // Empty array is valid (though may be required elsewhere)
            delete updatedErrors[field];
          } else {
            // Check if work experience entries have required fields
            const invalidEntries = value.filter(exp => 
              !exp || !exp.position || !exp.company || !exp.start_date
            );
            
            if (invalidEntries.length > 0) {
              updatedErrors[field] = 'Some work experiences are missing required fields';
            } else {
              delete updatedErrors[field];
            }
          }
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'phone':
        if (value) {
          if (typeof value !== 'string') {
            updatedErrors[field] = 'Phone number must be text';
          } else if (
            !/^[+]?[1-9][\d\s\-()]{7,15}$/.test(
              value.replace(/\s/g, '')
            )
          ) {
            updatedErrors[field] = 'Please enter a valid phone number';
          } else {
            delete updatedErrors[field];
          }
        } else {
          delete updatedErrors[field];
        }
        break;
        
      // URL field validation using URL_FIELDS constant
      case 'linkedin':
      case 'portfolio_url':
      case 'github_url':
      case 'website':
      case 'video_introduction_url':
        if (value) {
          if (typeof value !== 'string') {
            updatedErrors[field] = 'URL must be text';
          } else if (value.trim() === '') {
            delete updatedErrors[field]; // Empty string is valid (not required)
          } else {
            // Add protocol if missing
            let urlToCheck = value;
            if (!urlToCheck.startsWith('http://') && !urlToCheck.startsWith('https://')) {
              urlToCheck = 'https://' + urlToCheck;
            }
            
            try {
              new URL(urlToCheck);
              delete updatedErrors[field];
            } catch {
              updatedErrors[field] = 'Please enter a valid URL';
            }
          }
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'hourly_rate':
      case 'minimum_rate':
      case 'maximum_rate':
        if (value && (isNaN(value) || parseFloat(value) < PROFILE_CONSTANTS.VALIDATION_LIMITS.MIN_RATE)) {
          updatedErrors[field] = 'Please enter a valid positive number';
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'experience_years':
        if (value && (isNaN(value) || parseInt(value) < 0 || parseInt(value) > PROFILE_CONSTANTS.VALIDATION_LIMITS.MAX_EXPERIENCE_YEARS)) {
          updatedErrors[field] = `Please enter a valid number between 0 and ${PROFILE_CONSTANTS.VALIDATION_LIMITS.MAX_EXPERIENCE_YEARS}`;
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'bio':
        if (value && typeof value === 'string' && value.length > PROFILE_CONSTANTS.VALIDATION_LIMITS.BIO_MAX_LENGTH) {
          updatedErrors[field] = `Bio cannot exceed ${PROFILE_CONSTANTS.VALIDATION_LIMITS.BIO_MAX_LENGTH} characters`;
        } else if (value && typeof value !== 'string') {
          updatedErrors[field] = 'Bio must be text';
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'portfolio_description':
        if (value && typeof value === 'string' && value.length > PROFILE_CONSTANTS.VALIDATION_LIMITS.PORTFOLIO_DESCRIPTION_MAX_LENGTH) {
          updatedErrors[field] = `Portfolio description cannot exceed ${PROFILE_CONSTANTS.VALIDATION_LIMITS.PORTFOLIO_DESCRIPTION_MAX_LENGTH} characters`;
        } else if (value && typeof value !== 'string') {
          updatedErrors[field] = 'Portfolio description must be text';
        } else {
          delete updatedErrors[field];
        }
        break;
        
      case 'full_name':
        if (!value || (typeof value === 'string' && !value.trim())) {
          updatedErrors[field] = 'Full name is required';
        } else if (typeof value === 'string' && value.trim().length < 2) {
          updatedErrors[field] = 'Full name must be at least 2 characters';
        } else if (typeof value !== 'string') {
          updatedErrors[field] = 'Full name must be text';
        } else {
          delete updatedErrors[field];
        }
        break;
        
      // Array field validation
      case 'skills':
      case 'languages':
      case 'certifications':
      case 'soft_skills':
      case 'preferred_project_types':
        if (PROFILE_CONSTANTS.REQUIRED_FIELDS.includes(field) && (!value || (Array.isArray(value) && value.length === 0))) {
          updatedErrors[field] = 'This field is required';
        } else if (value && !Array.isArray(value)) {
          updatedErrors[field] = 'This field must be a list';
        } else {
          delete updatedErrors[field];
        }
        break;
        
      // Complex array field validation
      case 'work_samples':
      case 'portfolio_links':
        if (value && !Array.isArray(value)) {
          updatedErrors[field] = 'This field must be a list';
        } else if (Array.isArray(value)) {
          // Check if any items in the array are invalid
          const hasInvalidItems = value.some(item => {
            if (field === 'work_samples' && item && typeof item === 'object') {
              return !item.title; // Work samples should have a title
            }
            if (field === 'portfolio_links' && item && typeof item === 'object') {
              return !item.url; // Portfolio links should have a URL
            }
            return false;
          });
          
          if (hasInvalidItems) {
            updatedErrors[field] = `Some ${field.replace('_', ' ')} are missing required information`;
          } else {
            delete updatedErrors[field];
          }
        } else {
          delete updatedErrors[field];
        }
        break;

      default:
        // For required fields
        if (PROFILE_CONSTANTS.REQUIRED_FIELDS.includes(field)) {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            updatedErrors[field] = 'This field is required';
          } else {
            delete updatedErrors[field];
          }
        }
        break;
    }
    
    return updatedErrors;
  };
  
  // If setFieldErrors is provided, update state with the validation result
  if (setFieldErrors) {
    setFieldErrors(prev => validateField(prev));
    return null;
  }
  
  // Otherwise return the validation result directly
  return validateField({});
};

/**
 * Validates the entire profile form
 */
export const validateProfileForm = (profileData) => {
  let errors = {};
  
  // Validate all required fields
  PROFILE_CONSTANTS.REQUIRED_FIELDS.forEach(field => {
    const value = profileData[field];
    // First check if field is empty
    if (!value || (Array.isArray(value) && value.length === 0) || 
        (typeof value === 'string' && !value.trim())) {
      errors[field] = 'This field is required';
    } 
    // If not empty, validate using field-specific validation
    else {
      const fieldErrors = validateProfileField(field, value);
      if (fieldErrors && fieldErrors[field]) {
        errors[field] = fieldErrors[field];
      }
    }
  });
  
  // Validate optional fields that have values
  PROFILE_CONSTANTS.OPTIONAL_FIELDS.forEach(field => {
    const value = profileData[field];
    if (value !== undefined && value !== null && 
        !(typeof value === 'string' && !value.trim()) && 
        !(Array.isArray(value) && value.length === 0)) {
      const fieldErrors = validateProfileField(field, value);
      if (fieldErrors && fieldErrors[field]) {
        errors[field] = fieldErrors[field];
      }
    }
  });
  
  // Special case for rate range validation (cross-field validation)
  if (profileData.minimum_rate && profileData.maximum_rate) {
    const minRate = parseFloat(profileData.minimum_rate);
    const maxRate = parseFloat(profileData.maximum_rate);
    
    if (minRate > maxRate) {
      errors.minimum_rate = 'Minimum rate cannot be higher than maximum rate';
      errors.maximum_rate = 'Maximum rate cannot be lower than minimum rate';
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Calculates profile completion percentage
 */
export const calculateProfileCompletion = (profileData) => {
  const { REQUIRED_FIELDS, OPTIONAL_FIELDS, COMPLETION_WEIGHTS } = PROFILE_CONSTANTS;
  
  let completedRequired = 0;
  let completedOptional = 0;
  
  // Count completed required fields
  REQUIRED_FIELDS.forEach(field => {
    const value = profileData[field];
    
    if (value && 
        (Array.isArray(value) ? value.length > 0 : 
         typeof value === 'string' ? value.trim() !== '' : 
         value !== null && value !== undefined)) {
      completedRequired++;
    }
  });
  
  // Count completed optional fields
  OPTIONAL_FIELDS.forEach(field => {
    const value = profileData[field];
    
    // Special handling for work_experience
    if (field === 'work_experience' && Array.isArray(value) && value.length > 0) {
      // Check if at least one work experience has all required fields
      const hasValidExperience = value.some(exp => 
        exp && exp.position && exp.company && exp.start_date
      );
      
      if (hasValidExperience) {
        completedOptional++;
      }
    } 
    else if (value && 
        (Array.isArray(value) ? value.length > 0 : 
         typeof value === 'string' ? value.trim() !== '' : 
         value !== null && value !== undefined)) {
      completedOptional++;
    }
  });
  
  // Calculate weighted completion percentage
  const requiredPercentage = (completedRequired / REQUIRED_FIELDS.length) * COMPLETION_WEIGHTS.REQUIRED_WEIGHT;
  
  // Prevent division by zero if there are no optional fields
  const optionalPercentage = OPTIONAL_FIELDS.length > 0 
    ? (completedOptional / OPTIONAL_FIELDS.length) * COMPLETION_WEIGHTS.OPTIONAL_WEIGHT 
    : 0;
  
  return Math.round((requiredPercentage + optionalPercentage) * 100);
};

/**
 * Checks if a specific field has validation errors
 */
export const hasFieldError = (fieldErrors, field) => {
  return fieldErrors && fieldErrors[field];
};

/**
 * Gets the error message for a specific field
 */
export const getFieldError = (fieldErrors, field) => {
  return fieldErrors && fieldErrors[field] ? fieldErrors[field] : '';
};

/**
 * Validates file uploads (profile pictures, documents, etc.)
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize = PROFILE_CONSTANTS.VALIDATION_LIMITS.PROFILE_PICTURE_MAX_SIZE,
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    fieldName = 'file'
  } = options;
  
  const errors = [];
  
  if (!file) {
    errors.push(`${fieldName} is required`);
    return { isValid: false, errors };
  }
  
  if (file.size > maxSize) {
    errors.push(`${fieldName} size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
  }
  
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    errors.push(`${fieldName} must be one of: ${allowedTypes.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Gets missing profile fields for completion
 * @param {Object} profileData - The current profile data
 * @returns {Object} - Object containing missing required and optional fields with their labels
 */
export const getMissingProfileFields = (profileData) => {
  const { REQUIRED_FIELDS, OPTIONAL_FIELDS } = PROFILE_CONSTANTS;
  
  const fieldLabels = {
    // Required fields
    full_name: 'Full Name',
    email: 'Email',
    title: 'Professional Title',
    bio: 'Bio',
    location: 'Location',
    hourly_rate: 'Hourly Rate',
    experience_years: 'Years of Experience',
    availability_status: 'Availability Status',
    
    // Optional fields
    phone: 'Phone Number',
    website: 'Website',
    linkedin: 'LinkedIn Profile',
    certifications: 'Certifications',
    education: 'Education',
    industry_specialization: 'Industry Specialization',
    portfolio_description: 'Portfolio Description',
    portfolio_url: 'Portfolio URL',
    github_url: 'GitHub URL',
    work_experience: 'Work Experience',
    work_pace: 'Work Pace Preference',
    feedback_preference: 'Feedback Preference',
    collaboration_style: 'Collaboration Style',
    problem_solving_approach: 'Problem Solving Approach',
    work_style_description: 'Work Style Description',
    experience_level: 'Experience Level',
    timezone: 'Timezone',
    profile_picture: 'Profile Picture',
    video_introduction_url: 'Video Introduction'
  };
  
  const missingRequired = [];
  const missingOptional = [];
  
  // Check required fields
  REQUIRED_FIELDS.forEach(field => {
    const value = profileData[field];
    const isEmpty = !value || 
                    (Array.isArray(value) && value.length === 0) || 
                    (typeof value === 'string' && !value.trim());
    
    if (isEmpty) {
      missingRequired.push({
        field,
        label: fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      });
    }
  });
  
  // Check optional fields
  OPTIONAL_FIELDS.forEach(field => {
    const value = profileData[field];
    
    // Special handling for work_experience
    if (field === 'work_experience') {
      if (!Array.isArray(value) || value.length === 0) {
        missingOptional.push({
          field,
          label: fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        });
      } else {
        // Check if at least one work experience has all required fields
        const hasValidExperience = value.some(exp => 
          exp && exp.position && exp.company && exp.start_date
        );
        
        if (!hasValidExperience) {
          missingOptional.push({
            field,
            label: fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          });
        }
      }
    } else {
      const isEmpty = !value || 
                      (Array.isArray(value) && value.length === 0) || 
                      (typeof value === 'string' && !value.trim());
      
      if (isEmpty) {
        missingOptional.push({
          field,
          label: fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        });
      }
    }
  });
  
  return {
    missingRequired,
    missingOptional,
    totalMissing: missingRequired.length + missingOptional.length,
    hasMissingRequired: missingRequired.length > 0,
    hasMissingOptional: missingOptional.length > 0
  };
};
